
import { Ball, DrawerPoint, DrawerBrushType, DrawerSymmetryMode, DrawerColorMode, Vector2D } from '@/types/base';
import { SimulationConfig } from '@/types/config';

export class CustomDrawerRenderer {
    public static render(ctx: CanvasRenderingContext2D, balls: Ball[], config: SimulationConfig, globalHue: number) {
        if (!config.enableDrawer) return;

        balls.forEach(b => {
            if (!b.hitPoints || b.hitPoints.length === 0) return;
            
            const symmetryCount = config.drawerSymmetryCount || 1;
            const mode = config.drawerSymmetryMode || 'NONE';

            this.renderSymmetry(ctx, b, config, globalHue, mode, symmetryCount);
        });
    }

    private static renderSymmetry(ctx: CanvasRenderingContext2D, b: Ball, config: SimulationConfig, globalHue: number, mode: DrawerSymmetryMode, count: number) {
        if (mode === 'NONE') {
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, 1);
            return;
        }

        if (mode === 'MIRROR_X') {
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, 1);
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, -1, 1);
        } else if (mode === 'MIRROR_Y') {
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, 1);
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, -1);
        } else if (mode === 'MIRROR_XY') {
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, 1);
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, -1, 1);
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, -1);
            this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, -1, -1);
        } else if (mode === 'RADIAL') {
            const centerX = 0; // Assuming relative to center for symmetry
            const centerY = 0;
            for (let i = 0; i < count; i++) {
                const angle = (i / count) * Math.PI * 2;
                ctx.save();
                ctx.rotate(angle);
                this.renderBrush(ctx, b, b.hitPoints!, config, globalHue, 1, 1);
                ctx.restore();
            }
        }
    }

    private static renderBrush(ctx: CanvasRenderingContext2D, b: Ball, points: DrawerPoint[], config: SimulationConfig, globalHue: number, scaleX: number, scaleY: number) {
        const brush = config.drawerBrushType || 'SOLID';
        const colorMode = config.drawerColorMode || 'BALL';
        
        ctx.save();
        ctx.scale(scaleX, scaleY);
        
        if (config.drawerReverseMode) {
            points.forEach(p => {
                const color = this.getPointColor(p, b, config, globalHue, colorMode);
                this.drawSingleStroke(ctx, b.pos, p.pos, color, brush, config, p);
            });
        } else {
            // Continuous path needs segment by segment drawing for some brushes or gradients
            for (let i = 1; i < points.length; i++) {
                const p1 = points[i - 1];
                const p2 = points[i];
                const color = this.getPointColor(p2, b, config, globalHue, colorMode);
                this.drawSingleStroke(ctx, p1.pos, p2.pos, color, brush, config, p2);
            }
            // Connect to ball
            const lastP = points[points.length - 1];
            const color = this.getPointColor({ pos: b.pos, time: Date.now() } as any, b, config, globalHue, colorMode);
            this.drawSingleStroke(ctx, lastP.pos, b.pos, color, brush, config, { pos: b.pos, time: Date.now() } as any);
        }

        ctx.restore();
    }

    private static getPointColor(p: DrawerPoint, b: Ball, config: SimulationConfig, globalHue: number, mode: DrawerColorMode): string {
        switch (mode) {
            case 'BALL': return b.color;
            case 'RAINBOW': 
                const hue = (p.time * (config.drawerRainbowSpeed || 0.1)) % 360;
                return `hsl(${hue}, 100%, 50%)`;
            case 'VELOCITY':
                const speed = p.velocity || 0;
                const vHue = Math.min(speed * (config.drawerVelocityScale || 5), 240); // 0 to 240 (Blue to Red)
                return `hsl(${240 - vHue}, 100%, 50%)`;
            case 'FIXED': return config.drawerFixedColor || '#ffffff';
            case 'HUE_SHIFT':
                const shiftHue = (globalHue + (p.time % 3600) / 10) % 360;
                return `hsl(${shiftHue}, 100%, 50%)`;
            default: return b.color;
        }
    }

    private static drawSingleStroke(ctx: CanvasRenderingContext2D, p1: Vector2D, p2: Vector2D, color: string, brush: DrawerBrushType, config: SimulationConfig, pData: DrawerPoint) {
        const thickness = config.drawerLineThickness || 2;
        const opacity = config.drawerLineOpacity || 1;
        
        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = thickness;
        ctx.globalAlpha = opacity;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        switch (brush) {
            case 'NEON':
                ctx.shadowBlur = config.drawerGlowIntensity || 10;
                ctx.shadowColor = color;
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                // Inner highlight
                ctx.lineWidth = thickness * 0.4;
                ctx.strokeStyle = '#ffffff';
                ctx.stroke();
                break;
            
            case 'SKETCHY':
                ctx.lineWidth = thickness * 0.3;
                for (let i = 0; i < 3; i++) {
                    const off1 = (Math.random() - 0.5) * 4;
                    const off2 = (Math.random() - 0.5) * 4;
                    ctx.beginPath();
                    ctx.moveTo(p1.x + off1, p1.y + off2);
                    ctx.lineTo(p2.x + off2, p2.y + off1);
                    ctx.stroke();
                }
                break;

            case 'DOTTED':
                ctx.setLineDash([1, 4]);
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                break;

            case 'DASHED':
                ctx.setLineDash([ thickness * 2, thickness * 2 ]);
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                break;

            case 'GHOST':
                const age = Date.now() - pData.time;
                const lifetime = (config.drawerLineLifetime || 5) * 1000;
                ctx.globalAlpha = opacity * Math.max(0, 1 - age / lifetime);
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                break;

            case 'GLOW':
                ctx.shadowBlur = (config.drawerGlowIntensity || 10) * 2;
                ctx.shadowColor = color;
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                break;
            
            case 'CHROME':
                const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
                grad.addColorStop(0, '#555555');
                grad.addColorStop(0.5, '#ffffff');
                grad.addColorStop(1, '#555555');
                ctx.strokeStyle = grad;
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
                break;

            case 'ABSTRACT':
                ctx.moveTo(p1.x, p1.y);
                ctx.quadraticCurveTo(p1.x + (p2.x - p1.x)/2 + Math.random()*10, p1.y + (p2.y - p1.y)/2 + Math.random()*10, p2.x, p2.y);
                ctx.stroke();
                break;

            default:
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
        }

        ctx.restore();
    }
}
